import { z } from "zod";
import { toAsciiDigits } from "./digits.js";
import { SITE_CODE_PATTERN, SITE_CODE_MIN, SITE_CODE_MAX } from "./site.js";
const plainTextField = (max) => z
    .string()
    .max(max)
    .refine((v) => !/<[^>]+>/.test(v), "HTML not allowed")
    .refine((v) => !/javascript:/i.test(v), "JavaScript URLs not allowed")
    .refine((v) => !/on\w+\s*=/i.test(v), "Event handlers not allowed");
export const RESELLER_STATES = ["draft", "published", "archived"];
export const RESELLER_OPERATIONAL_NAME_MAX = 80;
export const RESELLER_SUPPORT_HOURS_MAX = 80;
export const RESELLER_SUPPORT_TEXT_MAX = 160;
export const RESELLER_SUPPORT_PHONE_MAX = 32;
export const RESELLER_TAGLINE_MAX = 120;
export const RESELLER_TERMS_COMPANY_NAME_MAX = 80;
export const RESELLER_LOGO_MAX_BYTES = 512 * 1024;
export const RESELLER_LOGO_MIN_DIMENSION = 32;
export const RESELLER_LOGO_MAX_DIMENSION = 1200;
export const RESELLER_LOGO_MIME_TYPES = [
    "image/jpeg",
    "image/png",
    "image/webp",
];
export const PUBLIC_RESELLER_UPLOAD_PATH_PREFIX = "/uploads/reseller";
export const RESELLER_LOGO_PUBLIC_PATH = /^\/uploads\/reseller\/[A-Za-z0-9_-]+\.(jpg|jpeg|png|webp)$/i;
export const WHATSAPP_E164_PATTERN = /^[1-9][0-9]{7,14}$/;
export function isResellerLogoPublicPath(path) {
    if (!path)
        return false;
    if (path.includes("..") || path.includes("?") || path.includes("#"))
        return false;
    if (/^[a-z]+:/i.test(path) || path.startsWith("//"))
        return false;
    return RESELLER_LOGO_PUBLIC_PATH.test(path);
}
/** Normalize operational code (same grammar as site codes). */
export function normalizeResellerCode(code) {
    return code.trim().toLowerCase();
}
export function isValidResellerCode(code) {
    const trimmed = normalizeResellerCode(code);
    return (trimmed.length >= SITE_CODE_MIN &&
        trimmed.length <= SITE_CODE_MAX &&
        SITE_CODE_PATTERN.test(trimmed));
}
export const resellerCodeSchema = z
    .string()
    .min(SITE_CODE_MIN)
    .max(SITE_CODE_MAX)
    .regex(SITE_CODE_PATTERN, "Invalid reseller code format");
/**
 * Digits-only E.164 national/international form used by the portal.
 * Accepts Persian/Arabic-Indic digits and common separators; stores ASCII digits.
 */
export function normalizeWhatsappE164(input) {
    const ascii = toAsciiDigits(input.trim());
    const digits = ascii.replace(/[+\s().-]/g, "").replace(/\D/g, "");
    if (!WHATSAPP_E164_PATTERN.test(digits))
        return null;
    return digits;
}
export const whatsappE164Schema = z
    .union([z.string(), z.null()])
    .optional()
    .transform((v, ctx) => {
    if (v == null || String(v).trim() === "")
        return null;
    const normalized = normalizeWhatsappE164(String(v));
    if (!normalized) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Invalid WhatsApp E.164 number" });
        return z.NEVER;
    }
    return normalized;
});
/** Display telephone: optional +, spaces, hyphens, parentheses around 8–15 digit core. */
export function normalizeSupportPhone(input) {
    const trimmed = toAsciiDigits(input.trim());
    if (!trimmed)
        return null;
    if (trimmed.length > RESELLER_SUPPORT_PHONE_MAX)
        return null;
    if (/[<>]|javascript:|https?:|\/\//i.test(trimmed))
        return null;
    if (!/^[+0-9()\s.-]+$/.test(trimmed))
        return null;
    const digits = trimmed.replace(/\D/g, "");
    if (digits.length < 8 || digits.length > 15)
        return null;
    if (!/^[1-9]/.test(digits))
        return null;
    return trimmed;
}
/**
 * One operator field → supportPhone + optional whatsappE164 dual-write for rc.21 portals.
 * Clear both when the field is empty.
 */
export function dualWriteResellerSupportFields(supportPhone) {
    if (supportPhone == null) {
        return { supportPhone: null, whatsappE164: null };
    }
    return {
        supportPhone,
        whatsappE164: normalizeWhatsappE164(supportPhone),
    };
}
export const supportPhoneSchema = z
    .union([z.string(), z.null()])
    .optional()
    .transform((v, ctx) => {
    if (v == null || String(v).trim() === "")
        return null;
    const normalized = normalizeSupportPhone(String(v));
    if (!normalized) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Invalid support phone" });
        return z.NEVER;
    }
    return normalized;
});
const optionalPlainNullable = (max) => z
    .union([plainTextField(max), z.null()])
    .optional()
    .transform((v) => {
    if (v == null)
        return null;
    const t = String(v).trim();
    return t === "" ? null : t;
});
export const createResellerProfileSchema = z.object({
    code: resellerCodeSchema,
    operationalName: plainTextField(RESELLER_OPERATIONAL_NAME_MAX),
    whatsappE164: whatsappE164Schema,
    supportPhone: supportPhoneSchema,
    supportHours: optionalPlainNullable(RESELLER_SUPPORT_HOURS_MAX),
    supportText: optionalPlainNullable(RESELLER_SUPPORT_TEXT_MAX),
    tagline: optionalPlainNullable(RESELLER_TAGLINE_MAX),
    termsCompanyName: optionalPlainNullable(RESELLER_TERMS_COMPANY_NAME_MAX),
});
export const updateResellerProfileSchema = z
    .object({
    version: z.number().int().positive(),
    operationalName: plainTextField(RESELLER_OPERATIONAL_NAME_MAX).optional(),
    whatsappE164: whatsappE164Schema,
    supportPhone: supportPhoneSchema,
    supportHours: optionalPlainNullable(RESELLER_SUPPORT_HOURS_MAX),
    supportText: optionalPlainNullable(RESELLER_SUPPORT_TEXT_MAX),
    tagline: optionalPlainNullable(RESELLER_TAGLINE_MAX),
    termsCompanyName: optionalPlainNullable(RESELLER_TERMS_COMPANY_NAME_MAX),
})
    .refine((d) => d.operationalName !== undefined ||
    d.whatsappE164 !== undefined ||
    d.supportPhone !== undefined ||
    d.supportHours !== undefined ||
    d.supportText !== undefined ||
    d.tagline !== undefined ||
    d.termsCompanyName !== undefined, { message: "At least one field to update is required" });
export const assignResellerSitesSchema = z.object({
    siteIds: z.array(z.string().min(1)).max(500),
});
export const publicResellerSchema = z.object({
    operationalName: plainTextField(RESELLER_OPERATIONAL_NAME_MAX),
    logoPath: z
        .string()
        .nullable()
        .refine((v) => v === null || isResellerLogoPublicPath(v), "Invalid reseller logo path"),
    whatsappE164: z
        .string()
        .nullable()
        .refine((v) => v === null || WHATSAPP_E164_PATTERN.test(v), "Invalid WhatsApp digits"),
    supportPhone: z.string().max(RESELLER_SUPPORT_PHONE_MAX).nullable(),
    supportHours: plainTextField(RESELLER_SUPPORT_HOURS_MAX).nullable(),
    supportText: plainTextField(RESELLER_SUPPORT_TEXT_MAX).nullable(),
    tagline: plainTextField(RESELLER_TAGLINE_MAX).nullable(),
    termsCompanyName: plainTextField(RESELLER_TERMS_COMPANY_NAME_MAX).nullable(),
});
//# sourceMappingURL=reseller.js.map